/**
 * Tenant-facing `/v1/billing/topup` request + response contracts (Epic #84 P8).
 *
 * POST /v1/billing/topup kicks off a ZPay (易支付) payment flow:
 *   1. Tenant picks amount (CNY cents, must be a whole-yuan multiple) +
 *      pay_type (wxpay).
 *   2. Platform locks the current `exchange_rate_policy` R onto a new
 *      `payment_orders` row (status='pending') and derives USD cents via
 *      floor(cny_cents / R) — the USD credit is what the customer's
 *      balance ledger actually receives on success.
 *   3. Platform calls ZPay `createOrder` with the CNY amount and returns
 *      a redirect / QR URL.
 *   4. Tenant browser is redirected; user pays in WeChat.
 *   5. ZPay notify round-trip flips the order to 'paid' and credits the
 *      USD balance. See `PublicZpayNotifyResult` (notify is plain-text).
 *
 * GET /v1/billing/topup/:order_id is status polling — tenant calls this
 * after the ZPay return URL lands on console-web, to confirm the order
 * reached 'paid' (or 'expired' / 'failed'). Authoritative signal lives
 * on the order row, written by the notify handler.
 *
 * CNY-integer-first input (PR P17, 2026-04-19). Previously the request
 * shape was `amountUsdCents` with server-side conversion to CNY, which
 * produced fractional-yuan CNY charges like ¥7.89 that the payment
 * aggregator accepts but the business does NOT want on the record
 * (operator reconciliation is in whole yuan; ZPay's merchant dashboard
 * shows CNY totals). The flipped shape forces whole-yuan amounts and
 * pushes the rounding (USD credit = floor(CNY / R)) onto our side of
 * the ledger — we prefer under-crediting the tenant by <1 cent over
 * over-charging the channel into negative L1 margin.
 *
 * Money shape: integer CNY cents on input (always a multiple of 100 =
 * whole yuan) + integer CNY cents + integer USD cents on output. Display
 * formatting (e.g. `¥12.34`) stays client-side — server never emits a
 * localized currency string.
 */

import type { PayType } from './public-billing.ts';

/**
 * Supported pay-type for *new* tenant-initiated topups. We keep the broader
 * `PayType` union in the shared public billing contract so historical orders
 * can still surface `alipay` on status / ops pages, but new topups are
 * intentionally locked to WeChat Pay only.
 */
export type TopupPayType = 'wxpay';

export interface CreateTopupRequest {
  /**
   * Integer CNY cents the tenant wishes to pay. MUST be a multiple of
   * 100 (whole yuan). Gateway enforces `TOPUP_MIN_CNY_CENTS <= n <=
   * TOPUP_MAX_CNY_CENTS` + the whole-yuan rule; see `/v1-billing.ts`
   * validator. `amountUsdCents` is derived server-side as
   * `floor(amountCnyCents / appliedRate)` and returned on the response.
   */
  amountCnyCents: number;
  /** Cashier channel — ZPay maps to `type` on the upstream request. */
  payType: TopupPayType;
}

/**
 * Server-side minimum / maximum for a single topup, in CNY cents.
 *
 *   - `TOPUP_MIN_CNY_CENTS` = 100 (¥1) — ZPay rejects sub-¥1 orders at
 *     the cashier; enforcing here gives the tenant `TOPUP_AMOUNT_TOO_SMALL`
 *     before the provider round-trip.
 *   - `TOPUP_MAX_CNY_CENTS` = 150_000 (¥1500) — corresponds to roughly
 *     $190 at the canonical 7.89 rate, a cap that triggers ops review
 *     via "contact sales" above that. Kept tighter than the raw CNY
 *     ceiling so upward rate drift doesn't silently expand the cap.
 *
 * Both bounds are enforced server-side (`v1-billing.ts` topup handler);
 * the tenant UI validates eagerly using these same constants. A future
 * `ops-config` surface could move these into D1, but Phase 4 treats them
 * as static constants to keep the validation graph small.
 */
export const TOPUP_MIN_CNY_CENTS = 100;
export const TOPUP_MAX_CNY_CENTS = 150_000;

export interface TopupQuoteResponse {
  /** Integer CNY cents supplied by the tenant UI. */
  amountCnyCents: number;
  /** Estimated integer USD cents at the current server-side policy rate. */
  amountUsdCents: number;
  /** Current `exchange_rate_policy.cny_per_usd` value used for topup quotes. */
  appliedRate: number;
}

export interface CreateTopupResponse {
  /** Our internal order id (`ord_<ulid>`). Tenant polls GET /v1/billing/topup/:id. */
  orderId: string;
  /** Merchant order id sent to ZPay. Same as `orderId` in Phase 1 (shape < 32 chars). */
  outTradeNo: string;
  /** Integer USD cents locked onto the order. Credits this amount on success. */
  amountUsdCents: number;
  /** Integer CNY cents customer pays ZPay. */
  amountCnyCents: number;
  /** `cny_per_usd` rate locked at order-creation time. Does not move post-creation. */
  appliedRate: number;
  /** Provider's cashier redirect URL. Present when mode='redirect'. */
  payUrl?: string;
  /** Provider's QR payload for native apps. Present when mode='qrcode'. */
  qrCodeUrl?: string;
  /** Optional server-rendered QR image URL (ZPay mapi mode). */
  imageUrl?: string;
}

export interface GetTopupStatusResponse {
  orderId: string;
  status: 'pending' | 'paid' | 'expired' | 'failed' | 'refunded';
  amountUsdCents: number;
  amountCnyCents: number;
  appliedRate: number;
  payType: PayType;
  createdAt: string;
  paidAt: string | null;
  expiredAt: string | null;
}

/**
 * Internal endpoint body for cron-driven expiry (called from control-
 * loops-worker's `payment_order_expirer`). Not part of the public
 * tenant surface.
 */
export interface ExpirePendingPaymentOrdersResponse {
  /** How many `pending` rows crossed the 30-minute boundary and were flipped to 'expired' on this tick. */
  expiredCount: number;
  /** When the handler ran (ISO8601). Used for log correlation only. */
  tickAt: string;
}
